import React, { useEffect, useRef } from 'react';
import { Box, useTheme } from '@ublend-npm/aulaui-core';

type OutlookHtmlBodyProps = {
  html: string;
  /** Fallback when body is plain text (not HTML). */
  isHtml?: boolean;
};

/**
 * Renders an email body.
 *
 * HTML emails are displayed inside a sandboxed <iframe> — this prevents
 * any scripts or external resources from running in the parent page context
 * while still faithfully rendering the original email layout.
 *
 * Plain-text emails are rendered as a <Typography> with white-space preserved.
 */
const OutlookHtmlBody = ({ html, isHtml = true }: OutlookHtmlBodyProps) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const theme = useTheme();

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe || !isHtml) return;

    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!doc) return;

    const textColor = theme.palette.mode === 'dark' ? '#E0E0E0' : '#212121';
    const linkColor = theme.palette.mode === 'dark' ? '#90CAF9' : '#1565C0';

    // Inject the raw HTML and add responsive styles and new tab base
    doc.open();
    doc.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8" />
          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <base target="_blank" />
          <style>
            * {
              box-sizing: border-box;
              max-width: 100% !important;
            }
            [id$="divRplyFwdMsg"] * {
              display: none;
            }
            hr:has(+ [id$="divRplyFwdMsg"]) {
              display: none !important;
            }
            .elementToProof {
              color: ${theme.palette.text.secondary} !important;
            }
            [class$="x_elementToProof"] {
              display:none;
            }
            html, body {
              margin: 0;
              padding: 0;
              font-family: Roboto, -apple-system, BlinkMacSystemFont, Arial, sans-serif;
              font-size: 14px;
              line-height: 1.5;
              word-break: break-word;
              overflow-x: hidden !important;
              width: 100% !important;
              max-width: 100% !important;
              color: ${textColor} !important;
            }
            table {
              width: 100% !important;
              max-width: 100% !important;
              table-layout: auto !important;
            }
            td, th {
              word-break: break-word;
            }
            img, video, iframe, embed, object {
              max-width: 100% !important;
              height: auto !important;
            }
            pre, code {
              white-space: pre-wrap !important;
              word-break: break-word !important;
            }
            a {
              color: ${linkColor};
              word-break: break-word;
            }
          </style>
        </head>
        <body>${html}</body>
      </html>
    `);
    doc.close();

    // Ensure all links open in a new tab via parent window.open
    const handleLinkClick = (e: MouseEvent) => {
      const target = (e.target as HTMLElement)?.closest('a');
      if (target && target.href && !target.href.startsWith('javascript:')) {
        e.preventDefault();
        window.open(target.href, '_blank', 'noopener,noreferrer');
      }
    };

    doc.addEventListener('click', handleLinkClick);

    // Auto-resize the iframe to fit its content height
    const resize = () => {
      if (iframe && doc.body) {
        const height = Math.max(
          doc.body.scrollHeight,
          doc.documentElement?.scrollHeight || 0,
          doc.body.offsetHeight || 0
        );
        iframe.style.height = `${height}px`;
      }
    };

    // Resize after paint so images/tables have loaded their dimensions
    requestAnimationFrame(resize);
    iframe.onload = resize;

    // Observe layout/DOM changes inside iframe
    const observer = new MutationObserver(resize);
    if (doc.body) {
      observer.observe(doc.body, {
        childList: true,
        subtree: true,
        attributes: true,
      });
    }

    const images = doc.querySelectorAll('img');
    images.forEach((img) => {
      if (!img.complete) {
        img.addEventListener('load', resize);
        img.addEventListener('error', resize);
      }
    });

    return () => {
      iframe.onload = null;
      doc.removeEventListener('click', handleLinkClick);
      observer.disconnect();
    };
  }, [html, isHtml, theme.palette.mode]);

  if (!isHtml) {
    return (
      <Box
        component="pre"
        sx={{
          m: 0,
          fontFamily: 'Roboto, -apple-system, Arial, sans-serif',
          fontSize: '14px',
          lineHeight: 1.5,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          overflowX: 'hidden',
          maxWidth: '100%',
          color: 'text.secondary',
        }}
      >
        {html}
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', overflowX: 'hidden', maxWidth: '100%' }}>
      <iframe
        ref={iframeRef}
        title="Email content"
        sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
        style={{
          width: '100%',
          maxWidth: '100%',
          border: 'none',
          display: 'block',
          minHeight: 80,
          overflowX: 'hidden',
        }}
      />
    </Box>
  );
};

export default OutlookHtmlBody;
